import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { Typography } from "@curves/ui-base";
import { PdfViewerDialog, LongPressButton } from "@curves/ui-shared";
import useInvoiceTriplicateUniformApi from "@Apis/useInvoiceTriplicateUniformApi";
import { isFunction } from "@Util/utils";

/**
 * 三聯發票折讓證明審核彈窗
 *
 * 預覽 confirmPath PDF，長按「核准」後呼叫 putInvoiceTriplicateUniformConfirmApi。
 * 由 FinanceDialogBox 控制 open／sourceData／refresh，勿在頁面直接掛載。
 */
const ReviewTriplicateAllowanceCertificateDialog = ({ open, sourceData = {}, onClose, refresh }) => {
	const { confirmPath, orderID, orderReturnID } = sourceData;
	const { putInvoiceTriplicateUniformConfirmApi } = useInvoiceTriplicateUniformApi();
	const [isSubmitting, setIsSubmitting] = useState(false);
	/** 開啟時快照 confirmPath，關閉動畫期間不因 sourceData 被清空而變成空字串 */
	const [pdfUrl, setPdfUrl] = useState("");
	/** 遞增以 remount LongPressButton（每次開啟或 API 失敗時重置長按進度） */
	const [resetKey, setResetKey] = useState(0);

	useEffect(() => {
		if (open) {
			setPdfUrl(confirmPath || "");
			setResetKey(k => k + 1);
		}
	}, [open, confirmPath]);

	const handleConfirm = useCallback(async () => {
		if (!orderID || !orderReturnID) return;
		setIsSubmitting(true);
		try {
			const result = await putInvoiceTriplicateUniformConfirmApi(orderID, orderReturnID);
			if (result === false) throw new Error();
			onClose();
			if (isFunction(refresh)) await refresh();
		} catch {
			// API 失敗：重置長按鈕，讓使用者可再次核准
			setResetKey(k => k + 1);
		} finally {
			setIsSubmitting(false);
		}
	}, [orderID, orderReturnID, putInvoiceTriplicateUniformConfirmApi, refresh, onClose]);

	return (
		<PdfViewerDialog open={open} pdfUrl={pdfUrl} onClose={onClose}>
			<PdfViewerDialog.Footer>
				<div className="review-action-bar">
					<Typography>請確認內容後長按核准按鈕</Typography>
					<LongPressButton key={resetKey} label="核准" successLabel="核准完成" onConfirm={handleConfirm} disabled={isSubmitting} />
				</div>
			</PdfViewerDialog.Footer>
		</PdfViewerDialog>
	);
};

ReviewTriplicateAllowanceCertificateDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	sourceData: PropTypes.object,
	onClose: PropTypes.func.isRequired,
	refresh: PropTypes.func,
};

export default ReviewTriplicateAllowanceCertificateDialog;
